import { LoadingProgress } from '@issuemanager/apps/client/components';
import {
  useAllQueries,
  useMonitorManageIssueFormLoad,
} from '@issuemanager/apps/client/hooks';
import { useIssueTemplateByIssueTypeAndIssueSubtype } from '@issuemanager/client/features/issue-templates';
import {
  useApproveForPublishing,
  useDeclineToDraft,
  useMoveToDraft,
  usePublishIssue,
  useSaveIssue,
  useSubmitToInReview,
} from '@issuemanager/client/features/issues';
import { useIssueTypeAndSubtypeSlas } from '@issuemanager/client/features/slas';
import { IssueStatusEnum, IssueTypeEnum } from '@issuemanager/constants';
import { Stack, useEventCallback } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import {
  IssueApprovePanel,
  IssueDeclinePanel,
  IssueDeclineWithCommentPanel,
} from '../components/panels/issue';
import { useHeaderButtons } from '../hooks/use-header-buttons';
import {
  ActionField,
  useManageIssueForm,
} from '../hooks/use-manage-issue-form';
import useResetFormData from '../hooks/use-reset-form-data';
import { useIssueOutletContext } from '../hooks/useIssueOutletContext';
import { ActionFields } from './action-fields';
import { Documents } from './documents';
import { IssueFields, RemediationSection } from './issue-fields';

export default function ManageIssue(): React.JSX.Element {
  const { t } = useTranslation(['common', 'issue', 'action']);
  const { code, issue, userRoles: issueUserRoles } = useIssueOutletContext();
  const monitorManageIssueLoad = useMonitorManageIssueFormLoad();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedPanel, setSelectedPanel] = useState<
    'decline' | 'approve' | 'declineToDraft' | null
  >(null);

  const [isEditing, setIsEditing] = useState(false);

  const SLAs = useIssueTypeAndSubtypeSlas({
    issueType: issue.data?.issueType?.name ?? '',
    issueSubtype: issue.data?.issueSubtype?.id,
  });

  const issueTemplate = useIssueTemplateByIssueTypeAndIssueSubtype({
    issueTypeId: issue.data?.issueType?.id ?? '',
    issueSubtypeId: issue.data?.issueSubtype?.id,
    enabled:
      (issue.data?.status === IssueStatusEnum.IN_REVIEW ||
        issue.data?.status === IssueStatusEnum.OPEN) &&
      issue.data &&
      !issue.data.selectedTemplate,
  });

  const isIssueCreator = Boolean(issueUserRoles.data?.isCreator);
  const isIssueTypeManager = Boolean(issueUserRoles.data?.isIssueTypeManager);
  const isIssueOwner = Boolean(issueUserRoles.data?.isOwner);
  const isDraftIssue = issue.data?.status === IssueStatusEnum.DRAFT;
  const canManageWorkflows =
    isIssueTypeManager || isIssueOwner || (isDraftIssue && isIssueCreator);

  const isIssueOpen =
    issue.data?.status === IssueStatusEnum.IN_REVIEW ||
    issue.data?.status === IssueStatusEnum.OPEN;
  const isAuditFinding =
    issue.data?.issueType?.name === IssueTypeEnum.AUDIT_FINDING;

  const messageKey = useMemo(() => {
    const isInProgress =
      issue.data?.status === IssueStatusEnum.IN_PROGRESS ||
      issue.data?.status === IssueStatusEnum.REMEDIATION_PLANNING ||
      issue.data?.status === IssueStatusEnum.IN_REMEDIATION;

    if (isInProgress) {
      if (isEditing) {
        return 'issue:manage.saveAndInitiate.success';
      }
      return 'issue:manage.initiate.success';
    }

    if (isEditing) {
      return 'issue:actions.saveAndApproveFromInReviewSuccess';
    }

    return 'issue:actions.approveFromInReviewSuccess';
  }, [isEditing, issue.data?.status]);

  const handleSubmitSuccess = (): void => {
    enqueueSnackbar(t(messageKey), {
      variant: 'success',
    });

    if (isEditing) {
      setIsEditing(false);
    }
  };

  const {
    manageIssueForm,
    assignIssueIsLoading,
    initiateWorkflowIsLoading,
    manageIssueHandleSave,
    manageIssueHandleSubmit,
    issueFields,
    actionFields,
    onAddAction,
    watched: { actions, executionType },
    fieldStates,
  } = useManageIssueForm({
    SLAs: SLAs.data,
    issueTemplate: issueTemplate.data,
    onSubmitSuccess: handleSubmitSuccess,
    isEditing,
  });

  const handleSaveSuccess = (): void => {
    enqueueSnackbar(t('issue:manage.save.success'), {
      variant: 'success',
    });
    setIsEditing(false);
  };

  const saveIssue = useSaveIssue({ code, onSuccess: handleSaveSuccess });

  const handleSubmitToInReviewSuccess = (): void => {
    enqueueSnackbar(t('issue:actions.submitSuccess'), {
      variant: 'success',
    });
  };

  const submitToInReviewMutation = useSubmitToInReview({
    code,
    onSuccess: handleSubmitToInReviewSuccess,
  });

  const handleMoveToDraftSuccess = (): void => {
    enqueueSnackbar(t('issue:actions.declineFromInReviewSuccess'), {
      variant: 'success',
    });
  };

  const moveToDraftMutation = useMoveToDraft({
    code,
    onSuccess: handleMoveToDraftSuccess,
  });

  const handleApproveForPublishingSuccess = (): void => {
    enqueueSnackbar('', {
      variant: 'success',
      description: (
        <Trans
          t={t}
          i18nKey='issue:actions.approveForPublishingSuccess'
          components={{ b: <b /> }}
          values={{ code, title: issue.data?.title }}
        />
      ),
    });
  };

  const approveForPublishingMutation = useApproveForPublishing({
    code,
    onSuccess: handleApproveForPublishingSuccess,
  });

  const handleApproveForPublishingClick = useEventCallback(() => {
    manageIssueHandleSubmit(async () => {
      approveForPublishingMutation.mutate();
    });
  });

  const handlePublishSuccess = (): void => {
    enqueueSnackbar('', {
      variant: 'success',
      description: (
        <Trans
          t={t}
          i18nKey='issue:actions.publishSuccess'
          components={{ b: <b /> }}
          values={{ code, title: issue.data?.title }}
        />
      ),
    });
  };

  const publishIssueMutation = usePublishIssue({
    code,
    onSuccess: handlePublishSuccess,
  });

  const handlePublishIssueClick = useEventCallback(() => {
    publishIssueMutation.mutate();
  });

  const handleDeclineToDraftSuccess = (): void => {
    setSelectedPanel(null);
    enqueueSnackbar('', {
      variant: 'success',
      description: (
        <Trans
          t={t}
          i18nKey='issue:actions.issueDeclined'
          components={{ b: <b /> }}
          values={{ code, title: issue.data?.title }}
        />
      ),
    });
  };

  const declineToDraftMutation = useDeclineToDraft({
    code,
    onSuccess: handleDeclineToDraftSuccess,
  });

  const handleDeclineToDraftClick = useEventCallback(
    (body?: { comment?: string }) => {
      declineToDraftMutation.mutate(body);
    },
  );

  const handleSubmitToInReviewClick = useEventCallback(() => {
    manageIssueHandleSubmit(async () => {
      submitToInReviewMutation.mutate();
    });
  });

  const handleMoveToDraftClick = useEventCallback(() => {
    moveToDraftMutation.mutate();
  });

  const handleSave = useEventCallback(() => {
    const issueStatus = issue.data?.status;
    if (!issueStatus) return;

    manageIssueHandleSave(saveIssue);
  });

  useAllQueries({
    queries: [issue],
    onSuccess: () => {
      monitorManageIssueLoad.stop();
    },
    onError: () => {
      monitorManageIssueLoad.error();
    },
  });

  const isLoading =
    issue.isFetching ||
    SLAs.isFetching ||
    issueTemplate.isFetching ||
    saveIssue.isPending;

  useHeaderButtons({
    manageIssueForm,
    assignIssueIsLoading,
    initiateWorkflowIsLoading,
    manageIssueHandleSubmit,
    isEditing,
    setIsEditing,
    isLoading,
    activateDeclinePanel: () => setSelectedPanel('decline'),
    activateApprovePanel: () => setSelectedPanel('approve'),
    handleSave,
    saveIssue,
    handleSubmitToInReview: isDraftIssue
      ? handleSubmitToInReviewClick
      : undefined,
    handleMoveToDraft:
      isIssueOpen && isIssueTypeManager ? handleMoveToDraftClick : undefined,
    handleApproveForPublishing:
      isAuditFinding && isIssueOpen && isIssueTypeManager
        ? handleApproveForPublishingClick
        : undefined,
    handlePublishIssue:
      isAuditFinding &&
      issue.data?.status === IssueStatusEnum.TO_BE_PUBLISHED &&
      isIssueTypeManager
        ? handlePublishIssueClick
        : undefined,
    submitToInReviewIsPending: submitToInReviewMutation.isPending,
    moveToDraftIsPending: moveToDraftMutation.isPending,
    approveForPublishingIsPending: approveForPublishingMutation.isPending,
    publishIssueIsPending: publishIssueMutation.isPending,
    activateDeclineToDraftPanel:
      isAuditFinding &&
      (issue.data?.status === IssueStatusEnum.TO_BE_PUBLISHED ||
        issue.data?.status === IssueStatusEnum.PENDING_ACCEPTANCE)
        ? () => setSelectedPanel('declineToDraft')
        : undefined,
    declineToDraftIsPending: declineToDraftMutation.isPending,
  });

  const { resetForm } = useResetFormData({
    manageIssueForm,
  });

  useEffect(() => {
    resetForm();
  }, [resetForm, issue.dataUpdatedAt]);

  const handleDragEnd = useEventCallback((draggedActions: ActionField[]) => {
    manageIssueForm.setValue('actions', draggedActions);
    manageIssueForm.trigger('actions');
  });

  const actionValues = useMemo(() => {
    if (issue.data?.actions.length) {
      return issue.data?.actions;
    }
    return [];
  }, [issue.data?.actions]);

  const renderContent = (): React.JSX.Element => {
    if (isLoading) {
      return <LoadingProgress height={600} />;
    }

    return (
      <FormProvider {...manageIssueForm}>
        <IssueFields
          title={issueFields.title}
          description={issueFields.description}
          severity={issueFields.severity}
          dueDate={issueFields.dueDate}
          govmapGroups={issueFields.govmapGroups}
          impact={issueFields.impact}
          ownerId={issueFields.ownerId}
          approverId={issueFields.approverId}
          watcherIds={issueFields.watcherIds}
          issueType={issue.data?.issueType?.name}
          datePublished={issueFields.datePublished}
          classification={issueFields.classification}
          resolution={issueFields.resolution}
          rootCause={issueFields.rootCause}
          recommendations={issueFields.recommendations}
          managementResponse={issueFields.managementResponse}
          remediation={issueFields.remediation}
        />

        <Documents
          control={manageIssueForm.control}
          name='attachments'
          isEditing={isEditing}
          attachments={issue.data?.attachments ?? []}
          onEdit={() => setIsEditing(true)}
        />

        {issue.data?.issueType?.name === IssueTypeEnum.AUDIT_FINDING &&
          issueFields.includeInAuditReport && (
            <Stack spacing={3}>{issueFields.includeInAuditReport}</Stack>
          )}

        <RemediationSection
          issueType={issue.data?.issueType?.name}
          resolution={issueFields.resolution}
          managementResponse={issueFields.managementResponse}
          remediation={issueFields.remediation}
        />

        <>
          <ActionFields
            issueFields={issueFields}
            actionFields={actionFields}
            actionsFormValues={actions}
            actionValues={actionValues}
            onAddAction={onAddAction}
            isEditing={isEditing}
            onEdit={canManageWorkflows ? () => setIsEditing(true) : undefined}
            executionType={executionType}
            onDragEnd={handleDragEnd}
            fieldStates={{
              actions: fieldStates.actions,
            }}
          />
          <IssueDeclinePanel
            actions={issue.data?.actions || []}
            code={code}
            isOpen={selectedPanel === 'decline'}
            onClose={() => setSelectedPanel(null)}
          />
          <IssueDeclineWithCommentPanel
            issueTitle={issue.data?.title ?? ''}
            isOpen={selectedPanel === 'declineToDraft'}
            onClose={() => setSelectedPanel(null)}
            onDecline={handleDeclineToDraftClick}
            isPending={declineToDraftMutation.isPending}
          />
          <IssueApprovePanel
            code={code}
            isOpen={selectedPanel === 'approve'}
            onClose={() => setSelectedPanel(null)}
          />
        </>
      </FormProvider>
    );
  };

  return (
    <Stack data-testid='manage-issue' spacing={3} p={4}>
      {renderContent()}
    </Stack>
  );
}
